import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'components/image';
import PoorText from 'components/poor-text';
import Link from 'components/link';
import PageHeading from 'components/page-heading';
import LinkButton, {
  sizes as linkButtonSizes,
  arrowDirections as linkButtonArrowDirections,
} from 'components/link-button/link-button';
import RecipeDetails from 'components/recipe-details';
import recipeMessageBus from 'js/recipe-message-bus';
import ParallaxHeroBackground from 'components/parallax-hero-background';
import { calculateInterpolatedValue } from 'js/calc-utils';
import { Parallax } from 'react-parallax';
import { imageSourceBreakpoints } from 'js/breakpoints';

const RecipePageHero = ({
  heading,
  introHtml,
  allRecipesLink,
  image,
  rating,
  cookingTime,
  difficultyLevel,
  lang = {
    cookingTimeIconAriaLabel: 'Cooking time',
    difficultyLevelIconAriaLabel: 'Difficulty level',
    ratingIconAriaLabel: 'Rating',
  },
}) => {
  const [currentRating, setCurrentRating] = useState(rating);
  useEffect(() => {
    const token = recipeMessageBus.subscribeToNewRating((msg, data) => {
      setCurrentRating(data);
    });

    return () => {
      recipeMessageBus.unsubscribeFromNewRating(token);
    };
  }, []);

  return (
    <div className="recipe-page-hero">
      <ParallaxHeroBackground
        backgroundColor="#FFAE9C"
        useParallaxOnMobile={false}
        className="recipe-page-hero__background"
      />
      <div className="recipe-page-hero__container">
        <div className="recipe-page-hero__image-holder">
          <Parallax
            style={{
              overflow: 'visible',
            }}
            renderLayer={percentage => {
              const maxPercentage = 1.3;
              const maxImageColPadding = 60;
              let imageColPadding = 0;

              if (percentage > maxPercentage) {
                imageColPadding = maxImageColPadding;
              } else {
                imageColPadding = calculateInterpolatedValue(
                  percentage,
                  maxImageColPadding,
                  maxPercentage
                );
              }

              return (
                <div
                  style={{
                    paddingRight: maxImageColPadding - imageColPadding,
                  }}
                  className="recipe-page-hero__image-holder-parallax-indent-element"
                >
                  <div className="recipe-page-hero__image-holder-overlay">
                    <Image
                      {...image}
                      sources={[
                        {
                          width: 406,
                          height: 357,
                          media: imageSourceBreakpoints.xs,
                        },
                        {
                          width: 314,
                          height: 276,
                          media: imageSourceBreakpoints.s,
                        },
                        {
                          width: 410,
                          height: 359,
                          media: imageSourceBreakpoints.m,
                        },
                        {
                          width: 625,
                          height: 549,
                          media: imageSourceBreakpoints.l,
                        },
                      ]}
                      fetchPriority="high"
                      className="recipe-page-hero__image image_size_recipe-page-hero"
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>
        <div className="recipe-page-hero__content">
          {allRecipesLink && (
            <LinkButton
              className="recipe-page-hero__all-recipes-link"
              size={linkButtonSizes.small}
              arrowDirection={linkButtonArrowDirections.left}
              {...allRecipesLink}
            />
          )}
          {heading && <PageHeading size="small" text={heading} />}
          {introHtml && (
            <PoorText
              text={introHtml}
              className="recipe-page-hero__intro poor-text_in-recipe-page-hero"
            />
          )}
          <RecipeDetails
            className="recipe-page-hero__details"
            cookingTime={cookingTime}
            difficultyLevel={difficultyLevel}
            rating={currentRating}
            cookingTimeIconAriaLabel={lang.cookingTimeIconAriaLabel}
            difficultyLevelIconAriaLabel={lang.difficultyLevelIconAriaLabel}
            ratingIconAriaLabel={lang.ratingIconAriaLabel}
            useFocusableDetails={true}
          />
        </div>
      </div>
    </div>
  );
};

RecipePageHero.propTypes = {
  heading: PropTypes.string,
  introHtml: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  allRecipesLink: PropTypes.exact(Link.propTypes),
  rating: PropTypes.string,
  cookingTime: PropTypes.string,
  difficultyLevel: PropTypes.string,
  lang: PropTypes.shape({
    cookingTimeIconAriaLabel: PropTypes.string.isRequired,
    difficultyLevelIconAriaLabel: PropTypes.string.isRequired,
    ratingIconAriaLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export default RecipePageHero;
